'use client';

import { useCallback, useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { useAtom, useSetAtom } from 'jotai';

import {
  audioQueueAtom,
  enqueueAudioAtom,
  updateAudioInQueueAtom,
} from '@/shared-survey';
import { base64ToAudioUrl } from '@/shared-utilities';
import { GENERATE_MODERATOR_SPEECH_V2 } from '@/survey-graphql';

export function useProjectResponseTTS(
  isAiVoiceEnabled = false,
  projectId?: string
) {
  const [getModeratorSpeech] = useMutation(GENERATE_MODERATOR_SPEECH_V2);
  const [speechAudios, setSpeechAudios] = useState<
    Array<{
      messageId: string;
      status: 'loading' | 'success' | 'error';
      audioUrl: string | null;
    }>
  >([]);

  // Audio queue integration
  const [audioQueue] = useAtom(audioQueueAtom);
  const enqueueAudio = useSetAtom(enqueueAudioAtom);
  const updateAudioInQueue = useSetAtom(updateAudioInQueueAtom);

  const getSpeech = useCallback(
    async (
      messageId: string,
      messageText: string,
      shouldInterrupt = true,
      overrideVoiceSettings = false
    ) => {
      // Don't call generateSpeech if AI voice is disabled
      if (!isAiVoiceEnabled && !overrideVoiceSettings) {
        return;
      }

      // Add to legacy state for backward compatibility
      setSpeechAudios((prev) => [
        ...prev,
        { messageId, status: 'loading', audioUrl: null },
      ]);

      // Enqueue in the new queue system
      enqueueAudio({
        messageId,
        messageText,
        audioUrl: null,
        status: 'loading',
        shouldInterrupt,
      });

      const markError = () => {
        setSpeechAudios((prev) =>
          prev.map((item) =>
            item.messageId === messageId ? { ...item, status: 'error' } : item
          )
        );
        updateAudioInQueue({ messageId, updates: { status: 'error' } });
      };

      if (!projectId) {
        console.error('[useProjectResponseTTS] missing projectId', {
          messageId,
        });
        markError();
        return;
      }

      try {
        const { data } = await getModeratorSpeech({
          variables: { input: { projectId, text: messageText } },
        });
        const base64Audio = data?.generateModeratorSpeechV2.audioData;

        if (!base64Audio) {
          console.error('[useProjectResponseTTS] no audio returned', {
            messageId,
          });
          markError();
          return;
        }

        const audioUrl = base64ToAudioUrl(base64Audio);

        setSpeechAudios((prev) =>
          prev.map((item) =>
            item.messageId === messageId
              ? { ...item, status: 'success', audioUrl }
              : item
          )
        );

        updateAudioInQueue({
          messageId,
          updates: { audioUrl, status: 'ready' },
        });
      } catch (error) {
        console.error(
          '[useProjectResponseTTS] getSpeech failed',
          { messageId },
          error
        );
        markError();
      }
    },
    [
      projectId,
      getModeratorSpeech,
      isAiVoiceEnabled,
      enqueueAudio,
      updateAudioInQueue,
    ]
  );

  const getSpeechForMessages = useCallback(
    async (
      messages: Array<{ id: string; text: string; transcript?: string }>
    ) => {
      // Don't call generateSpeech if AI voice is disabled
      if (!isAiVoiceEnabled) {
        return;
      }

      // Generate speech for all messages
      for (let i = 0; i < messages.length; i++) {
        const message = messages[i];
        const textToSpeak = message.transcript || message.text;
        if (textToSpeak) {
          // Only the first message in the batch should interrupt current playback
          // Subsequent messages should be queued normally
          await getSpeech(message.id, textToSpeak, i === 0);
        }
      }
    },
    [isAiVoiceEnabled, getSpeech]
  );

  function replaceMessageId(messageId: string, newMessageId: string) {
    setSpeechAudios((prev) =>
      prev.map((item) =>
        item.messageId === messageId
          ? { ...item, messageId: newMessageId }
          : item
      )
    );

    // Also update the ID in the atom queue
    updateAudioInQueue({
      messageId,
      updates: { messageId: newMessageId },
    });
  }

  return {
    getSpeech,
    getSpeechForMessages,
    speechAudios,
    replaceMessageId,
    audioQueue,
  };
}
